import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { tileService } from "../../../services/enhancedCrudService";
import { toast } from "react-toastify";

const TileDetail = () => {
  const { product_id } = useParams();
  const [tile, setTile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);

  const fetchTile = async () => {
    try {
      setLoading(true);
      const response = await tileService.getById(product_id);
      setTile(response.data);
    } catch (error) {
      console.error("Error fetching tile:", error);
      toast.error("Failed to load tile details");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await tileService.updateStatus(product_id, { status: newStatus });
      setTile((prev) => ({ ...prev, status: newStatus }));
      toast.success("Status updated successfully");
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");
    }
  };

  const handlePublishToggle = async () => {
    try {
      const newPublished = !tile.published;
      await tileService.updatePublishStatus(product_id, {
        published: newPublished,
      });
      setTile((prev) => ({ ...prev, published: newPublished }));
      toast.success(
        `Tile ${newPublished ? "published" : "unpublished"} successfully`
      );
    } catch (error) {
      console.error("Error updating publish status:", error);
      toast.error("Failed to update publish status");
    }
  };

  useEffect(() => {
    fetchTile();
  }, [product_id]);

  if (loading) {
    return (
      <div className="row">
        <div className="col-md-12">
          <div className="card">
            <div className="card-body">
              <div className="text-center">
                <div className="spinner-border" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!tile) {
    return (
      <div className="row">
        <div className="col-md-12">
          <div className="card">
            <div className="card-body">
              <div className="text-center">
                <h4>Tile not found</h4>
                <Link to="/products/tiles" className="btn btn-primary">
                  Back to Tiles
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="row">
      <div className="col-md-12">
        <div className="card">
          <div className="card-header">
            <h4 className="card-title">Tile Details</h4>
            <div className="card-actions">
              <Link
                to={`/products/tiles/edit/${product_id}`}
                className="btn btn-primary"
              >
                Edit Tile
              </Link>
              <Link to="/products/tiles" className="btn btn-secondary ml-2">
                Back to List
              </Link>
            </div>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-6">
                <div className="tile-images">
                  {tile.images && tile.images.length > 0 ? (
                    <>
                      <div className="main-image mb-3">
                        <img
                          src={tile.images[activeImage]}
                          alt={tile.name}
                          className="img-fluid rounded"
                          style={{
                            width: "100%",
                            height: "400px",
                            objectFit: "cover",
                          }}
                        />
                      </div>
                      {tile.images.length > 1 && (
                        <div className="thumbnail-images">
                          <div className="row">
                            {tile.images.map((image, index) => (
                              <div key={index} className="col-3 mb-2">
                                <img
                                  src={image}
                                  alt={`${tile.name} ${index + 1}`}
                                  className={`img-thumbnail cursor-pointer ${
                                    activeImage === index
                                      ? "border-primary"
                                      : ""
                                  }`}
                                  style={{
                                    width: "100%",
                                    height: "80px",
                                    objectFit: "cover",
                                  }}
                                  onClick={() => setActiveImage(index)}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="no-image-placeholder text-center">
                      <div
                        className="bg-light rounded d-flex align-items-center justify-content-center"
                        style={{ height: "400px" }}
                      >
                        <i className="fas fa-image fa-4x text-muted"></i>
                      </div>
                      <p className="text-muted mt-2">No images available</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="col-md-6">
                <div className="tile-info">
                  <h3>{tile.name}</h3>
                  <div className="mb-3">
                    <span className="h4 text-primary">
                      ₹{tile.price?.toLocaleString()}
                    </span>
                  </div>
                  <div className="mb-3">
                    <span
                      className={`badge badge-${
                        tile.is_active ? "success" : "danger"
                      }`}
                    >
                      {tile.is_active ? "Active" : "Inactive"}
                    </span>
                    <span
                      className={`badge badge-${
                        tile.published ? "info" : "warning"
                      }`}
                    >
                      {tile.published ? "Published" : "Draft"}
                    </span>
                  </div>

                  <div className="mb-3">
                    <h6>Description:</h6>
                    <p>{tile.description || "No description available"}</p>
                  </div>
                  {tile.link && (
                    <div className="mb-3">
                      <h6>External Link:</h6>
                      <a
                        href={tile.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary"
                      >
                        <i className="fas fa-external-link-alt me-1"></i>
                        {tile.link}
                      </a>
                    </div>
                  )}
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <div className="form-group">
                        <label>Tile Type:</label>
                        <p className="form-control-static">
                          {tile.type?.name || "N/A"}
                        </p>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label>Tile Size:</label>
                        <p className="form-control-static">
                          {tile.size?.name} ({tile.size?.dimensions})
                        </p>
                      </div>
                    </div>
                  </div>
                  {tile.specifications &&
                    Object.keys(tile.specifications).length > 0 && (
                      <div className="mb-3">
                        <h6>Specifications:</h6>
                        <div className="table-responsive">
                          <table className="table table-sm">
                            <tbody>
                              {Object.entries(tile.specifications).map(
                                ([key, value]) => (
                                  <tr key={key}>
                                    <td>
                                      <strong>{key}:</strong>
                                    </td>
                                    <td>{value}</td>
                                  </tr>
                                )
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  <div className="mb-3">
                    <h6>Actions:</h6>
                    <div className="btn-group" role="group">
                      <button
                        className={`btn btn-sm ${
                          tile.status === "active"
                            ? "btn-warning"
                            : "btn-success"
                        }`}
                        onClick={() =>
                          handleStatusChange(
                            tile.status === "active" ? "inactive" : "active"
                          )
                        }
                      >
                        {tile.status === "active" ? "Deactivate" : "Activate"}
                      </button>
                      <button
                        className={`btn btn-sm ${
                          tile.published ? "btn-warning" : "btn-info"
                        }`}
                        onClick={handlePublishToggle}
                      >
                        {tile.published ? "Unpublish" : "Publish"}
                      </button>
                      <Link
                        to={`/products/tiles/pdfs/${product_id}`}
                        className="btn btn-sm btn-dark me-2"
                      >
                        Manage PDFs
                      </Link>
                      {tile.link && (
                        <a
                          href={tile.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-info"
                        >
                          <i className="fas fa-external-link-alt me-1"></i>
                          View Link
                        </a>
                      )}
                    </div>
                  </div>
                  <div className="meta-info">
                    <small className="text-muted">
                      <strong>Created:</strong>{" "}
                      {tile.created_at
                        ? new Date(tile.created_at).toLocaleDateString()
                        : "N/A"}
                      <br />
                      <strong>Updated:</strong>{" "}
                      {tile.updated_at
                        ? new Date(tile.updated_at).toLocaleDateString()
                        : "N/A"}
                      <br />
                      <strong>Tile ID:</strong> {tile.product_id}
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TileDetail;
